import axios from '@/lib/axios'
import { buildAdminRequestConfig, type OrganizationId } from '@/lib/organization-scoping'
import type { QueryFunctionContext } from '@tanstack/react-query'
import type {
  AutomaticNavigation,
  StorefrontPreviewWrite,
  CreateDesignInput,
  Design,
  DesignIdInput,
  DesignInput,
  DesignPreviewSession,
  DesignScheduleStatesInput,
  DesignScheduleStatesResult,
  UpdateWebsiteBuilderInput,
} from './designs.types'

export interface DesignOrganizationScope {
  organizationId: OrganizationId
}

/** Scope for calls that act on one design; their Laravel routes require its id. */
export interface DesignScope extends DesignOrganizationScope {
  designId: DesignIdInput
}

/**
 * Entries of the design query keys that may name a design. The shared key builder can't mark an
 * entry required, and pages build keys for disabled queries before a design is chosen.
 */
export interface DesignShowKeyEntries extends DesignOrganizationScope {
  designId?: DesignIdInput | null | undefined
}

/** `DesignController::index` reads no filters; they only partition the cache. */
export type DesignIndexQueryKeyEntries = DesignOrganizationScope & {
  filters?: Record<string, unknown> | undefined
}

/** Query functions read only the key. */
type DesignQueryContext<TKey> = Pick<QueryFunctionContext<[TKey]>, 'queryKey'>

/** Writes to one design name it by `id`, as the design form's values do. */
interface DesignWriteScope extends DesignOrganizationScope {
  id: DesignIdInput
}

export async function getDesigns({
  queryKey: [{ organizationId, filters = {} }],
}: DesignQueryContext<DesignIndexQueryKeyEntries>) {
  return (
    (await axios.get<Design[]>(
      '/designs',
      buildAdminRequestConfig({ organizationId, params: { ...filters } })
    )) || []
  )
}

export async function getDesign({
  queryKey: [{ organizationId, designId }],
}: DesignQueryContext<DesignShowKeyEntries>) {
  const response = await axios.get<Design>(
    `/designs/${designId}`,
    buildAdminRequestConfig({ organizationId })
  )
  return response || {}
}

/** Null when the organization has no default design. */
export async function getDefaultDesign({
  queryKey: [{ organizationId }],
}: DesignQueryContext<DesignOrganizationScope>) {
  const response = await axios.get<Design | null>(
    '/designs/default',
    buildAdminRequestConfig({ organizationId })
  )
  return response || null
}

/** Without a design id, the menu a new design would get. */
export async function getAutomaticNavigation({
  queryKey: [{ organizationId, designId }],
}: DesignQueryContext<DesignShowKeyEntries>) {
  return (
    (await axios.get<AutomaticNavigation>(
      '/designs/automatic-navigation',
      buildAdminRequestConfig({
        organizationId,
        ...(designId ? { params: { design_id: designId } } : {}),
      })
    )) || { items: [] }
  )
}

export async function createDesign({
  organizationId,
  ...values
}: CreateDesignInput & DesignOrganizationScope) {
  return axios.post<Design>('/designs', values, buildAdminRequestConfig({ organizationId }))
}

/** 204 No Content: resolves to the empty body. */
export async function updateDesign({
  organizationId,
  id,
  ...values
}: DesignInput & DesignWriteScope) {
  return axios.put<string>(`/designs/${id}`, values, buildAdminRequestConfig({ organizationId }))
}

/** 204 No Content: resolves to the empty body. */
export async function updateWebsiteBuilder({
  organizationId,
  id,
  ...values
}: UpdateWebsiteBuilderInput & DesignWriteScope) {
  return axios.put<string>(
    `/website-builder/${id}`,
    values,
    buildAdminRequestConfig({ organizationId })
  )
}

/** 204 No Content: resolves to the empty body. A live scheduled design answers 409. */
export async function activateDesign({ organizationId, designId }: DesignScope) {
  return axios.post<string>(
    `/designs/${designId}/activate`,
    null,
    buildAdminRequestConfig({ organizationId })
  )
}

/** 204 No Content: resolves to the empty body. */
export async function enableDesignSchedule({ organizationId, designId }: DesignScope) {
  return axios.post<string>(
    `/designs/${designId}/schedule/enable`,
    null,
    buildAdminRequestConfig({ organizationId })
  )
}

/** 204 No Content: resolves to the empty body. */
export async function disableDesignSchedule({ organizationId, designId }: DesignScope) {
  return axios.post<string>(
    `/designs/${designId}/schedule/disable`,
    null,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function updateDesignScheduleStates({
  organizationId,
  schedules,
}: DesignOrganizationScope & { schedules: DesignScheduleStatesInput }) {
  return axios.post<DesignScheduleStatesResult>(
    '/designs/seasonal-schedules/bulk',
    {
      schedules: schedules.map((schedule) => ({
        design_id: schedule.designId,
        is_enabled: schedule.is_enabled,
      })),
    },
    buildAdminRequestConfig({ organizationId })
  )
}

/** Without a design id, a session for a design that is not saved yet. */
export async function createDesignPreviewSession({
  organizationId,
  designId = null,
  previewHost = null,
}: DesignShowKeyEntries & { previewHost?: string | null | undefined }) {
  const path = designId ? `/designs/${designId}/preview-session` : '/designs/preview-session'

  return axios.post<DesignPreviewSession>(
    path,
    { preview_host: previewHost },
    buildAdminRequestConfig({ organizationId })
  )
}

/** `GET /design`: the design the shop's storefront shows now, the way shoppers see it. */
export async function getStorefrontDesign({ organizationId }: DesignOrganizationScope) {
  return axios.get<Design | null>('/design', buildAdminRequestConfig({ organizationId }))
}

/**
 * Starts a read-only storefront preview of the shop's current design, the way the builder's
 * preview starts one: an API preview session (which also shows this shop's hidden pages), handed to
 * the storefront at `previewBaseUrl`. Resolves to the token for `/_preview/<token>/<path>` URLs.
 */
export async function startStorefrontPreview({
  organizationId,
  previewBaseUrl,
}: DesignOrganizationScope & { previewBaseUrl: string }): Promise<string> {
  const design = await getStorefrontDesign({ organizationId })

  if (!design) {
    throw new Error('This shop has no storefront design yet.')
  }

  const host = new URL(previewBaseUrl).host
  const session = await createDesignPreviewSession({
    organizationId,
    designId: design.id,
    previewHost: host,
  })
  const response = await putStorefrontPreviewSession({
    previewBaseUrl,
    writeKey: session.write_key,
    token: session.token,
    payload: {
      token: session.token,
      host,
      design: { ...design },
      updatedAt: new Date().toISOString(),
    },
  })

  if (!response.ok) {
    throw new Error("We couldn't start the storefront preview.")
  }

  return session.token
}

/** 204 No Content: resolves to the empty body. */
export async function deleteDesign({ organizationId, designId }: DesignScope) {
  return axios.delete<string>(`/designs/${designId}`, buildAdminRequestConfig({ organizationId }))
}

export function putStorefrontPreviewSession({
  previewBaseUrl,
  writeKey,
  token,
  signal,
  payload,
}: StorefrontPreviewWrite): Promise<Response> {
  return fetch(new URL('/api/admin-preview/session', `${previewBaseUrl}/`).toString(), {
    method: 'PUT',
    mode: 'cors',
    cache: 'no-store',
    ...(signal ? { signal } : {}),
    headers: {
      'content-type': 'application/json',
      authorization: `Bearer ${writeKey}`,
      'x-storefront-preview-token': token,
    },
    body: JSON.stringify(payload),
  })
}
